<template>
  <div
    class="relative isolate overflow-hidden bg-gradient-to-r from-primaryBG-50 via-white to-white px-6 py-16 sm:py-20 lg:py-24"
  >
    <!-- Decorative BG Pattern / Swirl -->
    <div class="absolute inset-0 -z-10 overflow-hidden">
      <svg
        class="absolute left-[max(50%,25rem)] top-0 h-[64rem] w-[128rem] -translate-x-1/2 stroke-gray-200
               [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)]"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="bg-pattern"
            width="200"
            height="200"
            x="50%"
            y="-1"
            patternUnits="userSpaceOnUse"
          >
            <path d="M100 200V.5M.5 .5H200" fill="none" />
          </pattern>
        </defs>
        <svg x="50%" y="-1" class="overflow-visible fill-gray-50">
          <path
            d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M-300.5 600h201v201h-201Z"
            stroke-width="0"
          />
        </svg>
        <rect
          width="100%"
          height="100%"
          stroke-width="0"
          fill="url(#bg-pattern)"
        />
      </svg>
    </div>

    <!-- Main Container with 3 columns on desktop -->
    <div class="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-3 gap-12">
      <!-- Main Content (First in HTML, but on desktop it will be to the right) -->
      <main class="order-1 lg:order-2 lg:col-span-2">
        <!-- Top Info -->
        <div class="space-y-4">
        
          <h1
            class="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl"
          >
          专科简介: {{ specialty_data.cn_name }} 
          </h1>
          <p class="text-base font-semibold text-primaryBG-600">
            {{ specialty_data.en_name }}
          </p>
          <h2 class="text-md font-light tracking-tight text-gray-800 sm:text-lg">
            通常也被称为
            <span
              v-for="name in specialty_data.cn_alts"
              :key="name"
              class="mr-2 bg-primaryBG-100 text-primaryBG-700 px-1 py-1 rounded-lg "
            >
              {{ name }}
            </span>
          </h2>
          <p class="text-base font-light text-primaryBG-800">
            Also Known As:
            <span
              v-for="name in specialty_data.en_alts"
              :key="name"
              class="mr-2 bg-primaryBG-100 text-primaryBG-700 px-1 py-1 rounded-lg whitespace-pre-wrap"
            >
              {{ name }}
            </span>
          </p>
          <p class="mt-4 text-gray-700 text-base sm:text-lg leading-relaxed">
            {{ specialty_data.short_brief }}
          </p>
        </div>

        <!-- Common Illnesses -->
        <div class="mt-10">
          <h3 class="text-xl font-bold text-primaryBG-700 mb-4">
            常见针对病症
          </h3>
          <ul class="space-y-6">
            <li
              v-for="illness in specialty_data.common_illnesses"
              :key="illness.illness_name"
              class="rounded-lg border border-gray-100 p-4
                     hover:bg-primaryBG-50 transition"
            >
              <h4 class="text-lg font-medium text-gray-900">
                {{ illness.illness_name }}
              </h4>
              <p class="text-gray-700 mt-1">
                {{ illness.illness_description }}
              </p>
            </li>
          </ul>
        </div>

        <!-- Common Services -->
        <div class="mt-6">
          <h3 class="text-xl font-bold text-primaryBG-700 mb-4">常见服务</h3>
          <ul class="space-y-6">
            <li
              v-for="service in specialty_data.common_services"
              :key="service.service_name"
              class="rounded-lg border border-gray-100 p-4
                     hover:bg-primaryBG-50 transition"
            >
              <h4 class="text-lg font-medium text-gray-900">
                {{ service.service_name }}
              </h4>
              <p class="text-gray-700 mt-1">
                {{ service.service_description }}
              </p>
            </li>
          </ul>
        </div>

        <!-- Hero Image (if you'd like to re-enable)
        <div class="mt-12 flex justify-center">
          <img
            class="w-full max-w-xl rounded-xl bg-gray-900 shadow-xl ring-1 ring-gray-400/10"
            :src="specialty_hero_img_url"
            :alt="specialty_hero_img_alt"
          />
        </div>
        -->

        <!-- Detailed Content (if you'd like to re-enable) -->
        <div class="mt-12">
          <p class="text-gray-700 leading-relaxed">
            {{ specialty_data.detailed_content }}
          </p>
          <!-- <h2 class="mt-10 text-2xl font-bold tracking-tight text-gray-900">
            需要寻找优质华人{{ specialty_data.cn_name }}医生？
          </h2>
          <p class="mt-4 text-gray-700">
            北美好医生汇集北美优质中文医疗资源，致力于让您随时随地享受华语医疗资源。
            下方是部分平台相关专科医生...
          </p> -->
        </div>
       
      </main>

      <!-- Left Sidebar (goes below main in mobile, remains on left in desktop) -->
      <aside class="order-2 lg:order-1 lg:col-span-1 space-y-10">
        <!-- Tip Box -->
        <div class="rounded-lg bg-primaryBG-100 p-4 shadow-md">
          <h3 class="text-lg font-semibold text-primaryBG-800">
            {{ specialty_data.cn_name }}小贴士
          </h3>
          <p class="text-primaryBG-900 text-sm mt-2">
            如果您发现无法解释的症状，请不要忽视，如果这些症状符合右侧本页列出的症状，请尽快咨询{{specialty_data.cn_name }}医生，一般情况下，您可直接致电{{ specialty_data.cn_name }}医生诊所，工作人员会告知您是否需要家庭医生转诊或如何使用您的健康医疗保险，发现不适时早期处理是预防复杂疾病的关键。如果您对医生的诊疗判断有疑问，您也可以通过我们的平台医生进行第二意见咨询。
          </p>
        </div>

        <!-- Doctors Highlight (Display up to 5) -->
        <div
          v-if="doctors_data && doctors_data.length"
          class="rounded-lg bg-white p-4 shadow-md border-l-4 border-primaryBG-500"
        >
          <h3 class="text-primaryBG-700 font-semibold text-lg">
            平台推荐医生
          </h3>

          <ul class="mt-4 space-y-4">
            <li
              v-for="doctor in doctors_data.slice(0, 5)"
              :key="doctor.npi"
              class="border-b last:border-b-0 pb-3"
            >
              <!-- Doctor Name -->
              <p class="text-gray-900 font-medium">
                {{ doctor.cn_name }}医生
                <span class="text-sm text-gray-500 ml-1">
                  ({{ doctor.en_firstname }} {{ doctor.en_lastname }})
                </span>
              </p>

              <!-- Specialty (Chinese/English) -->
              <p class="text-xs text-primaryBG-600 mt-1">
                纽约{{ doctor.cn_specialty_1 }}执照医生
              </p>

              <!-- Contact Info -->
              <div class="mt-2">
                <p class="text-sm text-gray-600">
                  
                </p>
                <a
                  :href="`/doctor/${doctor.cn_specialty_1}/${doctor.cn_name}医生`"
                  class="inline-block mt-2 text-sm text-primaryBG-700 hover:text-primaryBG-800 hover:underline"
                >
                  详细信息
                </a>
              </div>
            </li>
          </ul>
        </div>

        <!-- Another color panel or CTA Box -->
        <div class="rounded-lg bg-primaryBG-50 p-4 border-l-4 border-primaryBG-500">
          <h4 class="text-primaryBG-700 font-semibold">
            寻找更多{{ specialty_data.cn_name }}医疗资源？
          </h4>
          <p class="text-sm text-gray-800 mt-1">
            可点击上方 "找医生" 按钮，使用 "{{ specialty_data.cn_name }}" 、您现有的症状、或专科别称来寻找更多医疗资源，您也可以联系平台客服协助您寻找合适的医生。
          </p>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>


// (Optional) Icon imports
import { CloudArrowUpIcon, LockClosedIcon, ServerIcon } from '@heroicons/vue/20/solid'

// Component Props
const props = defineProps({
  specialty_data: {
    type: Object,
    required: true
  },
  // doctors_data is optional. If no data is passed, the doctor highlight won't show.
  doctors_data: {
    type: Array,
    required: false,
    default: () => []
  }
})
</script>

<style scoped>
/* Add any additional custom styling here */
</style>
